import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { AppServices } from '../../AppServices';
import { SharedMaterialsFullCompany } from '../../store/Models/SharedMaterials/DataModel';
import { ToxnotServiceClient } from '../../store/ToxnotServiceClient';
import { LoadingSpinner, LoadingSpinnerSize } from '../core/LoadingSpinner';
import { ToxnotReactComponentPaths } from '../core/ToxnotReactComponentPaths';
import * as Utilities from '../../utilities/Utilities';
import { ListDefinition, PrivateListDefinition, RestrictedAndRegulatoryListDefinition } from '../../store/Models/Lists/List';
import * as CustomerStateController from '../../store/StateControllers/Customers/CustomersStateController';
import { ApplicationState } from '../../store';
import { ToxnotComponentProperties } from '../core/BaseApplicationComponents/ToxnotComponent';
import { ScrollableContent, TopActionsAndScrollableContentContainer, TopActionButtons } from '../core/TopActionsAndScrollableContentContainer';
import { TableColumn, TableRow, ToxnotReactTable } from '../core/ToxnotReactTable';
import { CustomerRequest } from '../../store/Models/Customers/Customer';
import { CreateReportRequest, ReportTypeWithContext } from '../../store/Models/Api/CustomerApiRequestsAndResponses';
import { ReportTypeSelector } from '../core/ReportTypeSelector';
import { ReportTypesManager } from '../../store/Managers/ReportTypesManager';
import { ReportContext } from '../../store/Models/Customers/Customer';
import { OfflineProcessPollingManager } from '../../store/Managers/OfflineProcessPollingManager';
import { ProgressBar } from '../core/ProgressBar';
import { UserContext } from '../../store/Models/UserContext';
import { ToxnotSignInRequiredComponent } from '../core/BaseApplicationComponents/ToxnotSignInRequiredComponent';
import { RegulationManager } from '../../store/Managers/RegulationManager';

// ===============================================================================
//
// Modal to show and allow user to select list definitions
//

enum ListViewType {
    Favorite = "Favorite",
    Private = "Private"
}

type ShowListDefinitionsProps =
    ToxnotComponentProperties &
    CustomerStateController.MyCustomersState &
    typeof CustomerStateController.actionCreators &
    RouteComponentProps;

interface ShowListDefinitionsState {
    favoriteLists: RestrictedAndRegulatoryListDefinition[];
    privateLists: PrivateListDefinition[];
    isLoading: boolean;
    listView: ListViewType;
    selectedList: ListDefinition | null;
}

class ShowListDefinitionsModal extends ToxnotSignInRequiredComponent<ShowListDefinitionsProps, ShowListDefinitionsState> {
    private _serviceClient: ToxnotServiceClient;

    // ===============================================================================

    constructor(props: ShowListDefinitionsProps) {
        super(props);

        this._serviceClient = new ToxnotServiceClient();

        this.state = {
            favoriteLists: [],
            privateLists: [],
            isLoading: true,
            listView: ListViewType.Favorite,
            selectedList: null
        };
    }

    // ===============================================================================

    protected override getDisallowedRoles() {
        return [];
    }

    // ===============================================================================

    public componentDidMount() {
        this.getLists();
    }

    // ===============================================================================

    public renderConcreteComponent() {
        let body: JSX.Element;

        if (this.isLoading()) {
            body = (
                <div className="text-center">
                    <LoadingSpinner size={LoadingSpinnerSize.Medium} />
                </div>
            );
        }

        else {
            const request = CustomerStateController.getRequestFromState(this.props, this.props.respondingRequestIdForModals);
            body = this.renderBody(request);
        }

        return (
            <div id="select-list-modal" className="modal fade" role="dialog">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            {this.props.appServices.localizeString("Select a List for Generic Report")}
                        </div>
                        <div className="modal-body">
                            {body}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-default" data-dismiss="modal">{this.props.appServices.localizeString("Cancel")}</button>
                            {this.getSelectedListId() !== null && <button type="button" onClick={e => this.requestReports_click()} className="btn btn-toxnot-primary">{this.props.appServices.localizeString("Generate Response")}</button>}
                        </div>
                    </div>
                </div>
            </div>
        );     
    }

    // ===============================================================================

    private isLoading() {
        return this.state.isLoading || this.props.respondingRequestIdForModals === null;
    }

    // ===============================================================================

    private renderBody(request: CustomerRequest) {
        let rowModels: ListDefinition[];

        if (this.state.listView === ListViewType.Favorite) {
            rowModels = this.state.favoriteLists;
        }

        else if (this.state.listView === ListViewType.Private) {
            rowModels = this.state.privateLists;
        }

        else {
            throw new Error("Unsupported View Type.");
        }

        return (
            <div> 
                <ul id="list-type-nav" className="nav nav-pills tn-nav-pills">
                    <li role="presentation" className={this.getListViewActiveClass(ListViewType.Favorite)}>
                        <a role="button" onClick={e => this.viewType_click(ListViewType.Favorite)}>{this.props.appServices.localizeString("Favorite Lists")}</a>
                    </li>
                    <li role="presentation" className={this.getListViewActiveClass(ListViewType.Private)}>
                        <a role="button" onClick={e => this.viewType_click(ListViewType.Private)}>{this.props.appServices.localizeString("Private Lists")}</a>
                    </li>
                </ul>
                <div className="alert alert-info">
                    {this.props.appServices.localizeString("Your customer requested a generic report. To fullfill this, you must select a regulatory list to compare the requested materials against.")}
                </div>
                <div>
                    <p>
                        {request.requestData.reportTypesAndRegulationsToSpecificInfo["sgls-custom-report"] && (this.props.appServices.localizeString("Your customer requested the list: ") + request.requestData.reportTypesAndRegulationsToSpecificInfo["sgls-custom-report"] ?? "")}
                    </p>
                    <p>
                        {this.props.appServices.localizeString("Selected List: ") + (this.state.selectedList === null ? "" : this.state.selectedList.name)}
                    </p>
                </div>
                <div className="my-customers-request-detail-lists-container">
                    <TopActionsAndScrollableContentContainer shouldMakeTnSection={false}>
                        <ScrollableContent>
                            <ToxnotReactTable
                                localizer={this.props.appServices}
                                allRowModels={rowModels}
                                getRowForTable={this.getListRow}
                                headerRow={this.getHeaderRow()}
                                enablePaging={false}
                                rowClickEventHandler={this.row_click}
                            />
                        </ScrollableContent>
                    </TopActionsAndScrollableContentContainer>
                </div>
            </div>
        );
    }

    // ===============================================================================

    private getHeaderRow() {
        return [
            {
                value: this.props.appServices.localizeString("List Name"),
                isSortable: true,
                getSortableValue: (model: ListDefinition) => model.name
            } as TableColumn<ListDefinition>
        ];
    }

    // ===============================================================================

    private getListRow = (model: ListDefinition, index: number) => {
        return {
            cols: [model.name]
        } as TableRow;
    }

    // ===============================================================================

    private getListViewActiveClass(listView: ListViewType) {
        return this.state.listView === listView ? "active" : "";
    }

    // ===============================================================================

    private getLists = async() => {
        const apiResult = await this._serviceClient.favoriteAndPrivateListDefinitionsGetAll();

        this.setState({
            isLoading: true
        });

        if (!apiResult.success || apiResult.data === null) {
            this.props.appServices.displayApiErrorMessage();
            return;
        }

        this.setState({
            isLoading: false,
            favoriteLists: apiResult.data.favoriteLists,
            privateLists: apiResult.data.privateLists
        });
    }

    // ===============================================================================

    private getSelectedListId() {
        return this.state.selectedList?.listId ?? null;
    }

    // ===============================================================================
    //
    // UI Events
    //

    private viewType_click = (type: ListViewType) => {
        this.setState({
            listView: type
        });
    }

    // ===============================================================================

    private requestReports_click = () => {
        if (this.props.respondingRequestIdForModals === null) {
            throw new Error("Request ID for Modal was null");
        }

        let request = CustomerStateController.getRequestFromState(this.props, this.props.respondingRequestIdForModals);

        // TODO NF does this even work? we are assigning the selected list id to a local variable.
        // This never gets passed to the API.
        for(let report of request.requestData.reportTypesAndContexts) {
            const context = ReportTypesManager.getReportContextFromReportContextJson(report.contextJson);

            if (context !== null && ReportTypesManager.doesCustomerRequestRequireList(request)) {
                context.listId = this.getSelectedListId();
            }
        }

        const requestId = this.props.respondingRequestIdForModals
        this.props.respondToRequest(requestId, true);

        $('#select-list-modal').modal('hide');

        const url = `${ToxnotReactComponentPaths.MyCustomerDetailBasePath}/${this.props.currentlySelectedCustomerId}/Request/${requestId}`

        // If already on request detail page, dont navigate to it
        if (!window.location.pathname.includes(url)) {
            this.props.history.push(url);
        }
    }

    // ===============================================================================

    private row_click = (e: React.MouseEvent, model: ListDefinition) => {
        this.setState({
            selectedList: { ...model }
        });
    }
}

const ShowListDefinitionsModalConnect  = connect(
    (state: ApplicationState) => ({ appServices: state.appServices, ...state.myCustomersState }),
    CustomerStateController.actionCreators
)(withRouter(ShowListDefinitionsModal as any));


// ===============================================================================
//
// This modal informs the user about what exactly happens when they respond to a request.
//

type InformAboutRespondingModalProps = 
    ToxnotComponentProperties &
    CustomerStateController.MyCustomersState &
    typeof CustomerStateController.actionCreators;

class InformAboutRespondingModal extends ToxnotSignInRequiredComponent<InformAboutRespondingModalProps> {
    public renderConcreteComponent() {
        return (
            <div id="inform-about-responding-modal" className="modal fade" role="dialog">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4>{this.props.appServices.localizeString("Responding to a Request")}</h4>
                        </div>
                        <div className="modal-body">
                            <p>
                                {this.localizeString("You are about to respond to a request.") }
                            </p>
                            <ul>
                                <li>{this.localizeString("The requested report(s) will be created.")}</li>
                                <li>{this.localizeString("The requested parts will be published as passports containing the generated reports. All new passports will be published as restricted.")}</li>
                                <li>{this.localizeString("Your requesting customer will recieve an email with links with access to these passports. These links expire after 30 days.")}</li>
                            </ul>
                            <div className="checkbox">
                                <label>
                                    <input type="checkbox" onChange={this.dontShowAgain_onChange} /> {this.localizeString("Don't show this message again.")}
                                </label>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-default" data-dismiss="modal">{this.props.appServices.localizeString("Cancel")}</button>
                            <button type="button" onClick={e => this.requestReports_click()} className="btn btn-toxnot-primary">{this.props.appServices.localizeString("Generate Response")}</button>
                        </div>
                    </div>
                </div>
            </div>
        );      
    }
    // ===============================================================================

    protected override getDisallowedRoles() {
        return [];
    }

    // ===============================================================================
    //
    // UI Events
    //

    private requestReports_click = () => {
        this.props.respondToRequest(this.props.respondingRequestIdForModals, false);
        $('#inform-about-responding-modal').modal('hide');

        // Free users do not get redirected to the requests page
        // to view the progress, they instead view the progress in a modal
        // which we open here.
        if (!this.getUserCapabilities().myCustomersEnabled) {
            $('#select-report-type-modal').modal('show');
        }
    }

    // ===============================================================================

    private dontShowAgain_onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.props.setDontShowInfoModal(e.target.checked);
    }
}

const InformAboutRespondingModalConntect = connect(
    (state: ApplicationState) => ({ appServices: state.appServices, ...state.myCustomersState }),
    CustomerStateController.actionCreators
)(InformAboutRespondingModal as any);

// ===============================================================================
//
// Modal to select report types for creating reports for a customer.
//

type PickReportsModalProps =
    ToxnotComponentProperties &
    CustomerStateController.MyCustomersState &
    typeof CustomerStateController.actionCreators &
    RouteComponentProps;

interface PickReportsModalState {
    selectedReportTypes: { [reportType: string]: ReportContext | null }
    reportTypesAndRegulationsToSpecificInfo: { [reportTypeOrRegulation: string]: string }
}

class PickReportsForReportRequestModal extends ToxnotSignInRequiredComponent<PickReportsModalProps, PickReportsModalState> {
    private _manager: ReportTypesManager;

    constructor(props: PickReportsModalProps) {
        super(props);

        this._manager = new ReportTypesManager();

        this.state = {
            selectedReportTypes: {},
            reportTypesAndRegulationsToSpecificInfo: {},
        }
    }

    // ===============================================================================

    protected override getDisallowedRoles() {
        return [];
    }

    // ===============================================================================

    public renderConcreteComponent() {
        return (
            <div id="select-report-type-modal" className="modal fade" role="dialog">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4>{this.props.appServices.localizeString("Select the reports you want to share")}</h4>
                        </div>
                        <div className="modal-body">
                            {this.renderModalBody()}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-default" onClick={ e => this.closeBtn_click()}>{this.props.appServices.localizeString("Close")}</button>
                            {Object.keys(this.state.selectedReportTypes).length > 0 && this.props.pickReportsModalResponseState == CustomerStateController.PickReportsModalResponseState.Default && <button type="button" onClick={e => this.shareReportsWithCustomerSubmitButton_click()} className="btn btn-toxnot-primary">{this.props.appServices.localizeString("Share Reports")}</button>}
                        </div>
                    </div>
                </div>
            </div>
        );     
    }

    // ===============================================================================

    public renderModalBody() {
        let body: JSX.Element;

        // Because passports are created in the backend, a user must have an Exchange company
        // before they can generate reports
        if (this.props.sharedMaterialCompanies.length === 0) {
            body = (
                <div className="alert alert-warning">
                    {this.localizeString("Any reports you generate are stored in Material Passports on the Passport Library. You will need to create a company page first for those Material Passports.")} {<a role="button" onClick={e => this.navigateToCompanyPage()}>{this.localizeString("Click here")}</a>} {this.localizeString("to start this process.")}
                </div>  
            );
        }
        else if (this.props.respondingRequestIdForModals !== null && this.props.requestsResponding[this.props.respondingRequestIdForModals] && this.props.pickReportsModalResponseState == CustomerStateController.PickReportsModalResponseState.CreatingReport) {
            const state = this.props.requestsResponding[this.props.respondingRequestIdForModals];

            body = <ProgressBar
                currentProgressIndex={state.currentProgressItemIndex}
                totalNumItems={state.progressTotalNumItems}
                localizer={this.props.appServices}
                localizedTitle={OfflineProcessPollingManager.getFriendlyNameForOfflineProcessStage(state.processingStage, this.props.appServices)}
            />
        }
        else if (this.props.pickReportsModalResponseState == CustomerStateController.PickReportsModalResponseState.ReportCreated) {
            body = <div className="alert alert-success">{this.localizeString("Successfully created and sent emails to your customer(s).")}</div>
        }
        else {
            const reportTypesAndRegulations = this.getUserContextForSignedInUser().reportTypesAndRegulations;

            const customReportTypes = CustomerStateController.getCompanyCustomReportsFromState(this.props) ??
                reportTypesAndRegulations.allowedReportTypesForUser.filter(r => ReportTypesManager.allowedBulkReportTypes.includes(r));

            body = <ReportTypeSelector
                appServices={this.props.appServices}
                reportTypesAndContexts={ReportTypesManager.getReportTypesAndContextFromReportTypes(customReportTypes, reportTypesAndRegulations.reportTypeToRegulations)}
                reportSelectedCallback={this.onReportTypeOrRegulationSelect}
                renderOptions={{ onlyAllowSingleSelection: !this.getUserContextForSignedInUser().userCapabilities.myCustomersEnabled, showListInput: false, showRegulations: true }}
                isFreeUser={!this.getUserCapabilities().myCustomersEnabled}
                dynRegs={reportTypesAndRegulations.dynRegsForUser}
            />
        }

        return body;
    }

    // ===============================================================================

    private onReportTypeOrRegulationSelect = (selectedReports: { [reportType: string]: ReportContext | null }, reportTypesAndRegulationsToSpecificInfo: { [reportTypeOrRegulation: string]: string }) => {
        this.setState({
            selectedReportTypes: selectedReports,
            reportTypesAndRegulationsToSpecificInfo: { ...reportTypesAndRegulationsToSpecificInfo }
        });
    }

    // ===============================================================================

    private navigateToCompanyPage = () => {
        $('#select-report-type-modal').modal('hide');
        this.props.history.push(ToxnotReactComponentPaths.CompanyPagesManager);
    }

    // ===============================================================================
    //
    // UI Events
    //

    private shareReportsWithCustomerSubmitButton_click = () => {
        const request = this.props.requestForCreateReportRequest;

        if (request === null) {
            throw new Error("A CreateReportRequest must have already been set in the state before this modal was rendered.");
        }

        request.reportTypes = this._manager.getReportTypesWithContexts(this.state.selectedReportTypes);
        this.props.setRequestForCreateRequest(request);

        if (this.getUserContextForSignedInUser().userCapabilities.myCustomersEnabled) {
            this.props.doCreateRequestForCurrentCustomerOperation(true, this.props.history);
        }

        else {
            this.props.doCreateRequestForCurrentCustomerOperation(true);
        }
    }

    // ===============================================================================

    private closeBtn_click = () => {
        $('#select-report-type-modal').modal('hide');
        this.props.setPickReportsModalResponseState(CustomerStateController.PickReportsModalResponseState.Default);
    }
}

const PickReportsForReportRequestModalConnect = connect(
    (state: ApplicationState) => ({ appServices: state.appServices, ...state.myCustomersState }),
    CustomerStateController.actionCreators
)(withRouter(PickReportsForReportRequestModal as any));


// ===============================================================================
//
// This function returns all MyCustomer Modals
//

export function getMyCustomersModals(userContext: UserContext) {
    return (
        <React.Fragment>
            { /* Only users with the my customers functionality will be able to use this modal */}
            {userContext.userCapabilities.myCustomersEnabled && <ShowListDefinitionsModalConnect /> }
            <InformAboutRespondingModalConntect />
            <PickReportsForReportRequestModalConnect />
        </React.Fragment>
    );
}

